import { CircularProgress } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import {
  Body2Text,
  ConfirmationDialog,
  DefaultButton,
  DefaultCard,
  H4Text,
  HeadlineText,
  SubTitle2Text,
  colors,
  dateFormat,
} from 'design-system';
import { ReactElement, useState } from 'react';
import Inspection from 'src/models/helperTypes/Inspection';
import { DOWNLOAD_INSPECTION_REPORT } from 'src/store/quotation/requestIdentifiers';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'center',
      width: '100%',
    },
    actionSection: {
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    title: {
      fontSize: theme.spacing(2.5),
      fontWeight: 'bolder',
    },
    subTitle: {
      fontSize: theme.spacing(2),
      fontWeight: 600,
      color: colors.abbey,
    },
    rowMargin: {
      margin: theme.spacing(1),
    },
    inspectionDateMargin: {
      margin: theme.spacing(1.625, 0, 1, 0),
    },
    confirmationDialog: {
      width: theme.spacing(32.625),
    },
  }),
);

interface Props {
  inspection: Inspection;
  onDownload: (urlSalt: string) => void;
  onTransfer: (urlSalt: string) => void;
}

export default function InspectionCard({
  inspection,
  onDownload,
  onTransfer,
}: Props): ReactElement {
  const {
    urlSalt,
    customerName,
    carBrand,
    carModel,
    carYear,
    licensePlate,
    inspectionDate,
  } = inspection;
  const classes = useStyles();
  const inspectionReportStatus = useNetworkObserver(DOWNLOAD_INSPECTION_REPORT);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const title = (
    <HeadlineText className={classes.title}>{customerName}</HeadlineText>
  );
  const subTitle = (
    <SubTitle2Text
      className={classes.subTitle}
    >{`${carBrand} ${carModel} / ${carYear}`}</SubTitle2Text>
  );

  const isLoading = inspectionReportStatus === 'requesting';

  const reportButtonContent = isLoading ? (
    <CircularProgress data-testid="spinner" />
  ) : (
    'DESCARGAR REPORTE'
  );

  const confirmationMessage =
    '¿Estás seguro que deseas transferir esta inspección a esta cotización?';

  return (
    <>
      <DefaultCard title={title} smallSubtitle={subTitle}>
        <div className={classes.root}>
          <Body2Text style={{ color: '#B8B8B8' }}>Placa</Body2Text>
          <SubTitle2Text>{licensePlate}</SubTitle2Text>
          <Body2Text className={classes.inspectionDateMargin}>
            Inspeccionado el {dateFormat.dateFromServerString(inspectionDate)}
          </Body2Text>
          <div className={classes.actionSection}>
            <div className={classes.rowMargin}>
              <DefaultButton
                variant="outlined"
                onClick={(): void => {
                  onDownload(urlSalt);
                }}
                disabled={isLoading}
              >
                {reportButtonContent}
              </DefaultButton>
            </div>
            <div className={classes.rowMargin}>
              <DefaultButton
                onClick={(): void => setShowConfirmation(true)}
                data-testid="transfer-btn"
              >
                TRANSFERIR INSPECCIÓN
              </DefaultButton>
            </div>
          </div>
        </div>
      </DefaultCard>
      <ConfirmationDialog
        id="sales-web"
        data-testid="confirmation-dialog"
        open={showConfirmation}
        message={
          <H4Text style={{ width: '260px', fontWeight: 'normal' }}>
            {confirmationMessage}
          </H4Text>
        }
        rejectAction={(): void => setShowConfirmation(false)}
        confirmAction={(): void => {
          onTransfer(urlSalt);
        }}
      />
    </>
  );
}
